/**
 * Comparator modal UI enhancements: replace <tip> with tooltips and
 * <rating> with pentagon-based ratings (including fractional and 5+).
 * Listens for sc:dynamic-compare:loaded and runs inside the dialog shadow root.
 */
(function () {
  const EVENT_COMPARE_LOADED = 'sc:dynamic-compare:loaded';
  const DIALOG_SELECTOR = 'sc-dynamic-compare-dialog';
  const STYLES_ELEMENT_ID = 'comparator-modal-styles';

  const PENTAGON_VIEWBOX = '0 0 104.51 99.4';
  const PENTAGON_POINTS = '52.26 4.94 4.7 39.49 22.87 95.4 81.64 95.4 99.81 39.49 52.26 4.94';
  const RATING_MAX = 5;

  /**
   * Returns a unique id for gradients to avoid clashes in the shadow root.
   * @returns {string}
   */
  function uniqueGradientId() {
    return `comparator-pentagon-grad-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }

  /**
   * Creates one pentagon SVG: filled, unfilled, or partially filled.
   * @param {'full'|'empty'|'partial'} kind
   * @param {string} [gradientId] required for partial
   * @param {number} [partialPercent] 0–100 for partial
   * @returns {SVGSVGElement}
   */
  function createPentagonSVG(kind, gradientId, partialPercent) {
    const ns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('xmlns', ns);
    svg.setAttribute('viewBox', PENTAGON_VIEWBOX);
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('class', 'comparator-pentagon');

    if (kind === 'partial' && gradientId != null && partialPercent != null) {
      const defs = document.createElementNS(ns, 'defs');
      defs.innerHTML = `<linearGradient id="${gradientId}" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="${partialPercent}%" stop-color="var(--peintagon-color, #75a7bc)"></stop>
        <stop offset="${partialPercent}%" stop-color="var(--peintagon-color, #75a7bc)" stop-opacity="0"></stop>
      </linearGradient>`;
      svg.appendChild(defs);
    }

    const polygon = document.createElementNS(ns, 'polygon');
    polygon.setAttribute('points', PENTAGON_POINTS);
    const polygonClass = kind === 'empty'
      ? 'comparator-pentagon--empty'
      : kind === 'partial'
        ? 'comparator-pentagon--partial'
        : 'comparator-pentagon--fill';
    polygon.setAttribute('class', polygonClass);

    if (kind === 'partial' && gradientId) {
      polygon.style.fill = `url(#${gradientId})`;
    }

    svg.appendChild(polygon);
    return svg;
  }

  /**
   * Builds the pentagon rating element for a given value (e.g. 4, 4.56, or 5+).
   * @param {string} rawText e.g. "4", "4.56", "5+"
   * @param {number} index used for unique ids
   * @returns {HTMLElement}
   */
  function createPentagonRating(rawText, index) {
    const trim = String(rawText).trim();
    const is5Plus = /^5\+$/i.test(trim);
    const num = parseFloat(trim);
    const isNum = !Number.isNaN(num) && num >= 0 && num <= 5;

    const wrap = document.createElement('div');
    wrap.setAttribute('class', 'comparator-rating-pentagons');
    wrap.setAttribute('role', 'img');

    if (is5Plus) {
      for (let i = 0; i < RATING_MAX; i++) {
        wrap.appendChild(createPentagonSVG('full'));
      }
      const plus = document.createElement('span');
      plus.setAttribute('class', 'comparator-rating-plus');
      plus.setAttribute('aria-hidden', 'true');
      plus.textContent = '+';
      wrap.appendChild(plus);
      wrap.setAttribute('aria-label', '5 plus');
      return wrap;
    }

    if (!isNum) {
      wrap.setAttribute('aria-label', trim || 'No rating');
      return wrap;
    }

    const full = Math.floor(num);
    const fraction = num - full;
    const partialPercent = fraction > 0 ? Math.round(fraction * 100) : 0;
    const empty = RATING_MAX - full - (partialPercent > 0 ? 1 : 0);

    const gradId = partialPercent > 0 ? uniqueGradientId() : null;

    for (let i = 0; i < full; i++) {
      wrap.appendChild(createPentagonSVG('full'));
    }
    if (partialPercent > 0 && gradId) {
      wrap.appendChild(createPentagonSVG('partial', gradId, partialPercent));
    }
    for (let i = 0; i < empty; i++) {
      wrap.appendChild(createPentagonSVG('empty'));
    }

    wrap.setAttribute('aria-label', `${num} out of ${RATING_MAX}`);
    return wrap;
  }

  /**
   * Replaces <tip> elements with the theme tooltip (sc-tooltip + info icon).
   * @param {ShadowRoot} shadowRoot
   */
  function processTipElements(shadowRoot) {
    const tips = shadowRoot.querySelectorAll('tip');
    let i = 0;
    for (const tipEl of tips) {
      const tipContent = tipEl.textContent.trim();
      const uniqueId = `comparator-tip-${Date.now()}-${i}`;

      const tooltipHTML = `
        <sc-tooltip class="tooltip supports-anchor comparator-tooltip">
          <button type="button" class="tooltip__icon" aria-describedby="${uniqueId}-tooltip" popovertarget="${uniqueId}-tooltip">
            <span class="sr-only">Show more information</span>
            <svg class="icon" width="20" viewBox="0 0 20 20" aria-hidden="true">
              <path fill-rule="evenodd" d="M0 10C0 4.478 4.478 0 10 0c5.523 0 10 4.478 10 10 0 5.523-4.477 10-10 10-5.522 0-10-4.477-10-10zm11.125 2.002H8.989v-.141c.01-1.966.492-2.254 1.374-2.782.093-.056.19-.114.293-.178.73-.459 1.292-1.038 1.292-1.883 0-.948-.743-1.564-1.666-1.564-.851 0-1.657.398-1.712 1.533H6.304C6.364 4.693 8.18 3.5 10.294 3.5c2.306 0 3.894 1.447 3.894 3.488 0 1.382-.695 2.288-1.805 2.952l-.238.144c-.79.475-1.009.607-1.02 1.777V12zm.17 3.012a1.344 1.344 0 0 1-1.327 1.328 1.32 1.32 0 0 1-1.328-1.328 1.318 1.318 0 0 1 1.328-1.316c.712 0 1.322.592 1.328 1.316z" fill="currentColor"></path>
            </svg>
          </button>
          <div id="${uniqueId}-tooltip" role="tooltip" class="tooltip__content" popover="">
            ${escapeHtml(tipContent)}
            <i class="tooltip__arrow" aria-hidden="true"></i>
          </div>
        </sc-tooltip>`;

      const temp = document.createElement('div');
      temp.innerHTML = tooltipHTML;
      const tooltipEl = temp.firstElementChild;
      if (tooltipEl && tipEl.parentElement) {
        tipEl.parentElement.replaceChild(tooltipEl, tipEl);
      }
      i += 1;
    }
  }

  /**
   * Replaces <rating> elements with pentagon rating UI.
   * @param {ShadowRoot} shadowRoot
   */
  function processRatingElements(shadowRoot) {
    const ratings = shadowRoot.querySelectorAll('rating');
    let i = 0;
    for (const ratingEl of ratings) {
      const rawText = ratingEl.textContent.trim();
      const replacement = createPentagonRating(rawText, i);
      if (ratingEl.parentElement) {
        ratingEl.parentElement.replaceChild(replacement, ratingEl);
      }
      i += 1;
    }
  }

  /**
   * @param {string} text
   * @returns {string}
   */
  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Injects comparator modal CSS from the document into the dialog shadow root
   * so it applies to the modal (document styles do not cross shadow boundaries).
   * @param {ShadowRoot} shadowRoot
   */
  function injectComparatorModalStyles(shadowRoot) {
    const source = document.getElementById(STYLES_ELEMENT_ID);
    // Fallback when snippet is missing; does not include pentagon/rating classes.
    const css = source ? source.textContent : (
      '.metafield-single_line_text_field{position:relative}.comparator-tooltip{position:absolute;right:0}'
    );
    if (!css.trim()) return;
    const style = document.createElement('style');
    style.setAttribute('type', 'text/css');
    style.textContent = css;
    shadowRoot.appendChild(style);
  }

  /**
   * @param {ShadowRoot} shadowRoot
   */
  function runInShadowRoot(shadowRoot) {
    if (!shadowRoot) return;
    injectComparatorModalStyles(shadowRoot);
    processTipElements(shadowRoot);
    processRatingElements(shadowRoot);
  }

  document.addEventListener(EVENT_COMPARE_LOADED, function handleComparatorLoaded() {
    const dialog = document.querySelector(DIALOG_SELECTOR);
    if (!dialog || !dialog.shadowRoot) return;
    runInShadowRoot(dialog.shadowRoot);
  });

  // If the dialog is already in the DOM (e.g. app loaded before our script), run once
  function tryRunWhenDialogReady() {
    const dialog = document.querySelector(DIALOG_SELECTOR);
    if (dialog && dialog.shadowRoot) {
      runInShadowRoot(dialog.shadowRoot);
      return true;
    }
    return false;
  }

  if (document.readyState === 'complete') {
    setTimeout(tryRunWhenDialogReady, 500);
  } else {
    window.addEventListener('load', function () {
      setTimeout(tryRunWhenDialogReady, 500);
    });
  }
})();
